/* ============================================================================
   PROMPT CARD — enjin Flow RESIPI, "satu gambar untuk semua scene" (17 Okt 2026, PLAN_FLOW_RESIPI.md)
   ============================================================================
   Module 3–5 scene yang menuntut enjin `resipi`. Bentuk data BERBEZA daripada card FLOWNITY (di sana setiap
   scene ada prompt GAMBAR sendiri dalam `promptVideo{n}`); di sini:
     • Image Prompt  = set1.promptImage — SATU gambar AI, dipakai semua scene
     • Video Prompt  = set1.promptVideo{n} (scene 1: promptVideo / promptVideo1) — ditampal ke Flow seperti tertulis
   Peraturan medan: utils/resipiScene.js (KEMBAR skrip robot flow-resipi/20-resipi.js).

   Dua komponen RUPA SAHAJA — semua tulisan melalui pemanggil (WorkspaceView `updateDraftPrompt` +
   simpanan sedia ada), salin melalui `copyWithTag` (tag ##IMAGE-SET1## / ##VIDEO-SET1-SCENE{n}## = tag robot):
     KadResipiPrompt   — dalam card terbuka: kotak baca sahaja, klik = buka popup Edit Prompts
     ModalResipiPrompt — dalam popup Edit Prompts: kotak boleh edit
   Kelas Tailwind + cangkuk `sesaat-scenepil` = SAMA blok N-scene FLOWNITY dalam WorkspaceView, supaya gaya
   v3.9 sedia ada terpakai tanpa CSS baharu.
   ========================================================================== */
import { teksVideoBabak, medanVideoBabak, teksImejBabak, medanImejBabak, bilBabakResipi } from '../utils/resipiScene';

const ikonSalin = (aktif) => (aktif ? 'check' : 'content_copy');

/* Satu kotak Image Prompt untuk scene tertentu (resipi gambar berlainan). `onUbah` ada = boleh edit (popup);
   tiada = baca sahaja, klik buka popup (card). Dipakai juga oleh card 2 scene dalam WorkspaceView. */
export function KotakGambarBabak({ set, babak, onUbah, onBuka, onSalin, salinan, kunci, T, tajuk }) {
    const teks = teksImejBabak(set, babak);
    return (
        <div className="group/prompt">
            <div className="flex justify-between items-center mb-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.05em]">{tajuk || T('ws.resipi.imageScene', `Image Prompt · Scene ${babak}`, { n: babak })}</label>
                <button onClick={(e) => { e.stopPropagation(); onSalin?.('IMAGE', babak, teks, kunci); }} className="opacity-0 group-hover/prompt:opacity-100 p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-blue-500 transition-all" title={T('ws.common.copy', 'Copy')}>
                    <span className="material-symbols-outlined text-[12px]">{ikonSalin(salinan === kunci)}</span>
                </button>
            </div>
            <textarea
                value={teks}
                {...(onUbah ? { onChange: (e) => onUbah(medanImejBabak(set, babak), e.target.value) } : { readOnly: true, onClick: () => onBuka?.() })}
                className={`w-full ${onUbah ? 'h-24 text-sm' : 'h-16 text-[11px] cursor-pointer'} bg-blue-50/40 dark:bg-blue-900/10 p-2 rounded-lg border border-blue-100 dark:border-blue-900/30 leading-relaxed text-slate-600 dark:text-slate-300 resize-y font-mono`}
                placeholder={T('ws.resipi.imageScenePlaceholder', `Image Prompt Scene ${babak}...`, { n: babak })}
            />
        </div>
    );
}

export function KadResipiPrompt({ set, bilBabak, aktif = 1, onPilihBabak, onBuka, onSalin, salinan, idProduk, T, berlainan = false, tanpaGambar = false, tanpaVideo = false, gambarHasil = null }) {
    const n = bilBabakResipi(bilBabak);
    const babak = Math.min(Math.max(1, aktif), n);
    /* Images only: gambar yang robot simpan ke card (`gambarHasil.g1..g4`, extension gambarHasilKad.ts). */
    const senaraiHasil = [1, 2, 3, 4].map((b) => gambarHasil?.['g' + b]).filter((u) => typeof u === 'string' && /^https:/.test(u));
    const teksGambar = (set && typeof set.promptImage === 'string') ? set.promptImage : '';
    const teksVideo = teksVideoBabak(set, babak);
    const kGambar = `rimg_${idProduk}`;
    const kVideo = `rvid_${babak}_${idProduk}`;
    return (
        <>
            {senaraiHasil.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-3">
                    {senaraiHasil.map((u) => (
                        <a key={u} href={u} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                            <img src={u} alt="" loading="lazy" className="h-20 w-auto rounded-md border border-slate-200 dark:border-slate-700" />
                        </a>
                    ))}
                </div>
            )}
            {/* Image Prompt — satu untuk semua scene */}
            {!berlainan && !tanpaGambar && <div className="group/prompt mb-3">
                <div className="flex justify-between items-center mb-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.05em]">
                        Image Prompt <span className="normal-case font-semibold tracking-normal">· {T('ws.resipi.allScenes', 'semua scene')}</span>
                    </label>
                    <button onClick={(e) => { e.stopPropagation(); onSalin?.('IMAGE', null, teksGambar, kGambar); }} className="opacity-0 group-hover/prompt:opacity-100 p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-blue-500 transition-all" title={T('ws.common.copy', 'Copy')}>
                        <span className="material-symbols-outlined text-[12px]">{ikonSalin(salinan === kGambar)}</span>
                    </button>
                </div>
                <textarea
                    value={teksGambar}
                    readOnly onClick={() => onBuka?.()}
                    className="w-full h-16 bg-blue-50/40 dark:bg-blue-900/10 p-2 rounded-lg border border-blue-100 dark:border-blue-900/30 text-[11px] leading-relaxed text-slate-600 dark:text-slate-400 resize-none cursor-pointer hover:border-blue-300/60 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors font-mono"
                    placeholder={T('ws.resipi.imagePlaceholder', 'Image Prompt — satu gambar AI untuk semua scene...')}
                />
            </div>}
            {/* Pil scene */}
            <div className="flex gap-1.5 flex-wrap mb-3">
                {Array.from({ length: n }, (_, i) => {
                    const b = i + 1;
                    const on = babak === b;
                    return (
                        <button
                            key={b}
                            onClick={(e) => { e.stopPropagation(); onPilihBabak?.(b); }}
                            className={`sesaat-scenepil${on ? ' sesaat-scenepil--on' : ''} px-2.5 py-0.5 text-[11px] rounded-full border transition-all ${on ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-700 font-bold' : 'text-slate-400 border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500'}`}
                        >Scene {b}</button>
                    );
                })}
            </div>
            {/* Gambar berlainan: Image Prompt scene aktif */}
            {berlainan && (
                <div className="mb-3">
                    <KotakGambarBabak set={set} babak={babak} onBuka={onBuka} onSalin={onSalin} salinan={salinan} kunci={`rimg_${babak}_${idProduk}`} T={T} />
                </div>
            )}
            {/* Video Prompt scene aktif */}
            {!tanpaVideo && <div className="bg-slate-50 dark:bg-slate-800/40 rounded-lg border border-slate-100 dark:border-slate-800 p-3">
                <div className="group/prompt">
                    <div className="flex justify-between items-center mb-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.05em]">Video Prompt · Scene {babak}</label>
                        <button onClick={(e) => { e.stopPropagation(); onSalin?.('VIDEO', babak, teksVideo, kVideo); }} className="opacity-0 group-hover/prompt:opacity-100 p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-primary transition-all" title={T('ws.common.copy', 'Copy')}>
                            <span className="material-symbols-outlined text-[12px]">{ikonSalin(salinan === kVideo)}</span>
                        </button>
                    </div>
                    <textarea
                        value={teksVideo}
                        readOnly onClick={() => onBuka?.()}
                        className="w-full h-20 bg-slate-50 dark:bg-slate-800/40 p-2 rounded-lg border border-slate-100 dark:border-slate-800 text-[11px] leading-relaxed text-slate-600 dark:text-slate-400 resize-none cursor-pointer hover:border-primary/40 hover:bg-white dark:hover:bg-slate-800/60 transition-colors font-mono"
                        placeholder={T('ws.resipi.videoPlaceholder', `Video Prompt Scene ${babak}...`, { n: babak })}
                    />
                </div>
            </div>}
        </>
    );
}

export function ModalResipiPrompt({ set, bilBabak, onUbah, onSalin, salinan, idProduk, T, berlainan = false, tanpaGambar = false, tanpaVideo = false }) {
    const n = bilBabakResipi(bilBabak);
    const teksGambar = (set && typeof set.promptImage === 'string') ? set.promptImage : '';
    const kGambar = `modal_rimg_${idProduk}`;
    return (
        <>
            {/* Image Prompt — satu untuk semua scene, lebar penuh */}
            {!berlainan && !tanpaGambar && <div className="group/prompt p-4 bg-slate-50/60 dark:bg-slate-900/40 rounded-xl border border-slate-100 dark:border-slate-800">
                <div className="flex justify-between items-center mb-1">
                    <label className="text-[11px] font-bold text-slate-500 uppercase flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span> Image Prompt
                        <span className="normal-case font-semibold">· {T('ws.resipi.allScenesLong', `satu gambar AI dipakai semua ${n} scene`, { n })}</span>
                    </label>
                    <button onClick={() => onSalin?.('IMAGE', null, teksGambar, kGambar)} className="opacity-0 group-hover/prompt:opacity-100 p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-500 hover:text-blue-500 transition-all" title={T('ws.common.copy', 'Copy')}>
                        <span className="material-symbols-outlined text-[14px]">{ikonSalin(salinan === kGambar)}</span>
                    </button>
                </div>
                <textarea
                    value={teksGambar}
                    onChange={(e) => onUbah?.('promptImage', e.target.value)}
                    className="w-full h-24 bg-blue-50/50 dark:bg-blue-900/10 p-3 rounded-lg border border-blue-200 dark:border-blue-900/40 text-sm leading-relaxed text-slate-700 dark:text-slate-200 resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                    placeholder={T('ws.resipi.imagePlaceholder', 'Image Prompt — satu gambar AI untuk semua scene...')}
                />
            </div>}
            {/* Video Prompt setiap scene — lajur bertindan menegak pada telefon (sama blok FLOWNITY) */}
            <div className={`flex-1 grid gap-4 items-stretch grid-cols-1 ${n <= 3 ? 'md:grid-cols-3' : n === 4 ? 'md:grid-cols-4' : 'md:grid-cols-5'}`}>
                {Array.from({ length: n }, (_, i) => {
                    const b = i + 1;
                    const teks = teksVideoBabak(set, b);
                    const k = `modal_rvid${b}_${idProduk}`;
                    return (
                        <div key={b} className="flex flex-col gap-3 p-4 bg-slate-50/60 dark:bg-slate-900/40 rounded-xl border border-slate-100 dark:border-slate-800 h-full">
                            <h4 className="text-[11px] font-extrabold text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] px-3 py-1 bg-white dark:bg-slate-800 rounded-md self-start shadow-sm">Scene {b}</h4>
                            {berlainan && (
                                <KotakGambarBabak set={set} babak={b} onUbah={onUbah} onSalin={onSalin} salinan={salinan} kunci={`modal_rimg${b}_${idProduk}`} T={T} />
                            )}
                            {!tanpaVideo && <div className="group/prompt flex flex-col flex-1 min-h-0">
                                <div className="flex justify-between items-center mb-1">
                                    <label className="text-[11px] font-bold text-slate-500 uppercase flex items-center gap-1">
                                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span> Video Prompt
                                    </label>
                                    <button onClick={() => onSalin?.('VIDEO', b, teks, k)} className="opacity-0 group-hover/prompt:opacity-100 p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-500 hover:text-primary transition-all" title={T('ws.common.copy', 'Copy')}>
                                        <span className="material-symbols-outlined text-[14px]">{ikonSalin(salinan === k)}</span>
                                    </button>
                                </div>
                                <textarea
                                    value={teks}
                                    onChange={(e) => onUbah?.(medanVideoBabak(set, b), e.target.value)}
                                    className="w-full flex-1 min-h-[160px] bg-white dark:bg-slate-800/40 p-3 rounded-lg border border-slate-200 dark:border-slate-700 text-sm leading-relaxed text-slate-600 dark:text-slate-300 resize-none focus:ring-2 focus:ring-primary focus:border-primary font-mono"
                                    placeholder={T('ws.resipi.videoPlaceholder', `Video Prompt Scene ${b}...`, { n: b })}
                                />
                            </div>}
                        </div>
                    );
                })}
            </div>
        </>
    );
}
